<script setup>
import XlSkeleton from '../../xl/XlSkeleton.vue';
import { partOfDay, viewerTimeZone } from '../../../helpers/greeting';

defineProps({
  dateLabel: { type: String, default: '' },
  firstName: { type: String, default: '' },
  sentence: { type: String, default: '' },
  // What needs somebody now, drawn bold, then the calmer rest (prototype
  // .greet .hl). Without them the plain sentence is shown, as on first run.
  lead: { type: String, default: '' },
  rest: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  title: { type: String, default: '' },
});

// The viewer's own clock (XN-4673): the browser knows it, and "Good morning"
// at four in the afternoon in Belgrade was the hub's morning, not theirs.
const salutation = (name) => {
  const hello = `Good ${partOfDay(new Date(), viewerTimeZone())}`;
  return name ? `${hello}, ${name}` : hello;
};
</script>

<template>
  <header class="greeting" data-testid="overview-greeting">
    <p v-if="!title" class="greeting__date xl-section-heading" data-testid="greeting-date">{{ dateLabel }}</p>
    <h1 class="greeting__hello">{{ title || salutation(firstName) }}</h1>

    <!-- A skeleton, never half a sentence: a partly-assembled claim about the
         day is worse than an obvious placeholder. -->
    <!-- In the sentence's own line box, so the sentence lands where it was. -->
    <div v-if="loading" class="greeting__sentence" data-testid="greeting-loading"><XlSkeleton variant="text" width="48ch" /></div>
    <p v-else-if="lead || rest" class="greeting__sentence" data-testid="greeting-sentence">
      <b v-if="lead" class="greeting__lead" data-testid="greeting-lead">{{ lead }}</b>
      <template v-if="lead && rest">{{ ' ' }}</template>
      <span v-if="rest" class="greeting__rest">{{ rest }}</span>
    </p>
    <p v-else class="greeting__sentence" data-testid="greeting-sentence">{{ sentence }}</p>
  </header>
</template>

<style scoped>
.greeting {
  margin-bottom: 24px;
  font-family: var(--xl-font-family);
}

.greeting__date {
  /* Typography is .xl-section-heading's; only the spacing is this page's. */
  margin: 0 0 6px;
}

/* 34px and 17px are off the type scale on purpose. The scale has no display
   size — it stops at --xl-font-size-xl, 30px — and this is the largest text on
   the page. Snap them to the scale once a display token exists, not before. */
.greeting__hello {
  margin: 0 0 10px;
  font-size: 34px;
  line-height: 1.15;
  font-weight: var(--xl-font-weight-bold);
  color: var(--xl-color-on-surface);
  overflow-wrap: anywhere;
}

.greeting__sentence {
  margin: 0;
  max-width: 62ch;
  font-size: 17px;
  line-height: 1.5;
  color: var(--xl-color-on-surface);
}

/* The prototype's .hl: what needs you, in the ink's own weight; the rest
   after it in the muted colour. */
.greeting__lead {
  font-weight: var(--xl-font-weight-semibold, 600);
  color: var(--xl-color-on-surface);
}

.greeting__rest {
  color: var(--xl-color-outline);
}
</style>
